// @file Vuex Store for showing input dialogs, similar to browser `prompt`s.
import type { DispatchableAction } from '@@/types'
import type { Module } from 'vuex'
interface InputDialogDispatchableAction extends DispatchableAction {
  type: string
  payload?: object
}

type InputDialogFunction = ({ inputValue }: { inputValue: string }) => void
type InputDialogAction = InputDialogDispatchableAction | InputDialogFunction

export interface HeaderActionButton {
  iconName: string
  iconAlt: string
  actions: Function[]
  ariaLabel?: string
  title?: string
}

interface InputDialogPayload {
  iconSrc?: string | null
  iconAlt?: string | null
  title: string
  subtitle?: string
  inputPlaceholder?: string
  inputLabel?: string
  inputValue?: string
  inputType?: string
  inputMaxLength?: number | null
  inputAriaLabel?: string
  submitButtonText?: string
  cancelButtonText?: string
  submitActions: InputDialogAction[]
  validationActions: InputDialogAction[]
  shouldFadeIn?: boolean
  isValid?: boolean
  validationMessage?: string
  headerTopEndContent?: HeaderActionButton | null
}

interface InputDialogState extends Required<InputDialogPayload> {
  isOpen: boolean
  submitButtonDisabled: boolean
}

const blankState = (): InputDialogState => ({
  isOpen: false,
  iconSrc: null,
  iconAlt: null,
  title: '',
  subtitle: '',
  inputPlaceholder: '',
  inputLabel: '',
  inputValue: '',
  inputType: 'text',
  inputMaxLength: null,
  inputAriaLabel: '',
  submitButtonText: '',
  cancelButtonText: '',
  submitActions: [],
  validationActions: [],
  shouldFadeIn: true,
  submitButtonDisabled: false,
  isValid: true,
  validationMessage: '',
  headerTopEndContent: null,
})

const InputDialogModule: Module<InputDialogState, object> = {
  namespaced: true,
  state: blankState,
  getters: {
    inputValue(state): string {
      return state.inputValue
    },
  },
  mutations: {
    RESET_DIALOG(state): void {
      Object.assign(state, blankState())
    },
    OPEN_DIALOG(state, dialogState: Partial<InputDialogState>): void {
      Object.assign(state, dialogState)
      state.isOpen = true
    },
    SET_SUBMIT_BUTTON_DISABLED(state, { disable }: { disable: boolean }): void {
      state.submitButtonDisabled = disable
    },
    UPDATE_INPUT_VALUE(state, value: string): void {
      state.inputValue = value
    },
    SET_VALIDATION_MESSAGE(state, message: string): void {
      state.validationMessage = message
    },
    SET_INPUT_IS_VALID(state, isValid: boolean): void {
      state.isValid = isValid
    },
  },
  actions: {
    openInputDialog({ commit, state, dispatch }, params: Partial<InputDialogState>): void {
      commit('OPEN_DIALOG', params)
      void dispatch('executeActions', state.validationActions)
    },
    updateInputValue({ commit, state, dispatch }, value: string): void {
      commit('UPDATE_INPUT_VALUE', value)
      void dispatch('executeActions', state.validationActions)
    },
    executeActions({ state, dispatch }, actions: InputDialogAction[]): void {
      actions.forEach((action): void => {
        if (typeof action === 'function') {
          action({ inputValue: state.inputValue })
          return
        }
        const payload = { ...action.payload, inputValue: state.inputValue }
        void dispatch(action.type, payload, { root: true })
      })
    },
    setSubmitButtonDisable({ commit, state }, { disable }: { disable: boolean }): void {
      if (state.submitButtonDisabled !== disable) commit('SET_SUBMIT_BUTTON_DISABLED', { disable })
    },
    submit({ commit, dispatch, state }): void {
      if (state.submitButtonDisabled) return
      void dispatch('executeActions', state.submitActions)
      commit('RESET_DIALOG')
    },
    setValidationMessage({ commit }, message: string): void {
      commit('SET_VALIDATION_MESSAGE', message)
    },
    setInputIsValid({ commit }, isValid: boolean): void {
      commit('SET_INPUT_IS_VALID', isValid)
    },
    cancel({ commit }): void {
      commit('RESET_DIALOG')
    },
  },
}

export default InputDialogModule
export type { InputDialogState }
